import { Canvas2D } from '@/components/Canvas2D'
import { Toolbar } from '@/components/Toolbar'
import { LevelPanel } from '@/components/LevelPanel'
import { DrawPanel } from '@/components/DrawPanel'
import { OpeningPanel } from '@/components/OpeningPanel'
import { PropPanel } from '@/components/PropPanel'
import { Inspector } from '@/components/Inspector'
import { lazy, Suspense } from 'react'
import { useEditor } from '@/state/store'
import { useAutosave } from '@/hooks/useAutosave'
import { useKeyboard } from '@/hooks/useKeyboard'
import { useShareLink } from '@/hooks/useShareLink'
import { clearShareHash } from '@/lib/share'

// Code-split the 3D stack (three / r3f / postprocessing) so Design mode never loads it.
const Visualizer3D = lazy(() =>
  import('@/components/Visualizer3D').then((m) => ({ default: m.Visualizer3D })),
)

export default function App() {
  useShareLink() // must run before useAutosave's local-load (it bails on a share hash)
  useAutosave()
  useKeyboard()
  const mode = useEditor((s) => s.mode)
  const readOnly = useEditor((s) => s.readOnly)

  return (
    <div className="app">
      <Toolbar />
      {readOnly && <ShareBanner />}
      {mode === 'visualize' ? (
        <main className="viz-wrap">
          <Suspense fallback={<div className="viz-loading">Loading 3D…</div>}>
            <Visualizer3D />
          </Suspense>
        </main>
      ) : (
        <div className="workspace">
          <aside className="left-rail">
            <LevelPanel />
            {readOnly ? (
              <ViewHelpCard />
            ) : (
              <>
                <DrawPanel />
                <OpeningPanel />
                <PropPanel />
                <HelpCard />
              </>
            )}
          </aside>
          <main className="canvas-wrap">
            <Canvas2D />
          </main>
          <aside className="right-rail">
            {/* `disabled` fieldset natively neutralises every form control while viewing. */}
            <fieldset className="rail-fieldset" disabled={readOnly}>
              <Inspector />
            </fieldset>
          </aside>
        </div>
      )}
    </div>
  )
}

/** Top banner shown while viewing someone else's shared, read-only design. */
function ShareBanner() {
  const forkShared = useEditor((s) => s.forkShared)
  const name = useEditor((s) => s.project.name)
  return (
    <div className="share-banner">
      <span className="share-banner-text">
        👁️ Viewing a shared design (read-only) — <b>{name}</b>
      </span>
      <button
        className="primary-btn"
        onClick={() => {
          forkShared()
          clearShareHash() // a refresh shouldn't re-enter view mode
        }}
        title="Make your own editable copy"
      >
        Fork to edit
      </button>
    </div>
  )
}

/** Left-rail card shown in read-only share view (no editing tools). */
function ViewHelpCard() {
  return (
    <section className="panel help-card">
      <h3>Viewing a shared design</h3>
      <ul>
        <li>This is a read-only copy — editing is disabled.</li>
        <li><b>V</b> select · <b>H</b> or drag empty space — pan · scroll — zoom</li>
        <li>Switch floors above; open <b>Visualize ▶</b> to see it in 3D</li>
        <li>Hit <b>Fork to edit</b> (top banner) to make your own editable copy</li>
      </ul>
    </section>
  )
}

function HelpCard() {
  return (
    <section className="panel help-card">
      <h3>How to draw</h3>
      <ul>
        <li><b>R</b> — drag a rectangular space</li>
        <li><b>P</b> — click corners; click the first point (gold) or double-click to close</li>
        <li><b>V</b> — select; drag body to move</li>
        <li><b>D</b> — opening tool: click near a wall to add a door/window/gate; drag to slide it</li>
        <li><b>Place prop</b> — pick from the catalog, click to drop; <b>[</b>/<b>]</b> rotate ±15°</li>
        <li>Drag <b>gold dots</b> = corners · <b>gray squares</b> = whole edges</li>
        <li>Double-click an <b>edge</b> to add a corner · a <b>corner</b> to remove it</li>
        <li><b>Shift-click</b> — add/remove from selection · <b>⌘A</b> — select all</li>
        <li><b>H</b> or drag empty space — pan · scroll — zoom</li>
        <li><b>⌫</b> — delete selected · <b>⌘Z</b> — undo</li>
      </ul>
    </section>
  )
}
